import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import thomasMuller from '../../assets/thomas-muller.png';
import celsionLogo from '../../assets/celsion-bank.png';

const BLOCK: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const ITEM: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.3, ease: [0.16, 0.77, 0.47, 0.97] } },
};

const viewport = { once: true, amount: 0.1 };

const MeshBorder: React.FC = () => (
  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 0 }}>
    <svg width="100%" height="100%" viewBox="0 0 900 420" preserveAspectRatio="none">
      <path d="M 0,80 Q 300,60 600,90 T 900,75" stroke="rgba(29,47,65,0.06)" strokeWidth="0.8" fill="none" />
      <path d="M 0,340 Q 300,360 600,330 T 900,345" stroke="rgba(29,47,65,0.06)" strokeWidth="0.8" fill="none" />
      <path d="M 60,0 Q 40,210 65,420" stroke="rgba(29,47,65,0.05)" strokeWidth="0.8" fill="none" />
      <path d="M 840,0 Q 860,210 835,420" stroke="rgba(29,47,65,0.05)" strokeWidth="0.8" fill="none" />
      <path d="M 0,420 Q 200,200 420,0" stroke="rgba(29,47,65,0.04)" strokeWidth="0.8" fill="none" />
      <path d="M 900,420 Q 700,200 480,0" stroke="rgba(29,47,65,0.04)" strokeWidth="0.8" fill="none" />
    </svg>
  </Box>
);

const Testimonials: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      component="section"
      data-nosnippet
      sx={{
        bgcolor: '#f8f9fb',
        color: '#1D2F41',
      }}
      id="testimonials"
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 8, md: 12 } }}>

        {/* Title */}
        <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={BLOCK}>
          <motion.div variants={ITEM} style={{ willChange: 'transform' }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#1D2F41',
                fontFamily: '"Calibri", sans-serif',
                lineHeight: 1.5,
                bgcolor: '#EBEDF0',
                display: 'inline-block',
                pt: '10px',
                pl: '10px',
                pb: '10px',
                pr: '10px',
                mb: 5,
              }}
            >
              {t('testimonials.label')}
            </Typography>
          </motion.div>
        </motion.div>

        {/* Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={BLOCK} style={{ willChange: 'transform' }}>
          <Box
            sx={{
              bgcolor: '#ffffff',
              position: 'relative',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'stretch' },
              p: { xs: 4, md: 5 },
              gap: { xs: 4, md: 0 },
            }}
          >
            <MeshBorder />

            {/* Photo + name */}
            <Box
              component={motion.div}
              variants={ITEM}
              sx={{
                flexShrink: 0,
                width: { xs: '100%', md: '200px' },
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
                zIndex: 1,
              }}
              style={{ willChange: 'transform' }}
            >
              <Box
                component="img"
                src={thomasMuller}
                alt={t('testimonials.name')}
                loading="lazy"
                sx={{
                  width: '160px',
                  height: '180px',
                  objectFit: 'cover',
                  objectPosition: 'center 15%',
                  display: 'block',
                  filter: 'grayscale(100%)',
                  clipPath: 'polygon(0 0, 88% 0, 100% 12%, 100% 82%, 94% 100%, 6% 100%, 0 90%)',
                }}
              />
              <Box>
                <Typography sx={{ fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif', fontSize: '1rem', fontWeight: 700, color: '#1D2F41', mb: 0.5 }}>
                  {t('testimonials.name')}
                </Typography>
                <Typography sx={{ fontFamily: '"Calibri", sans-serif', fontSize: '0.85rem', color: 'rgba(29, 47, 65, 0.55)', lineHeight: 1.4 }}>
                  {t('testimonials.role')}
                </Typography>
              </Box>
            </Box>

            {/* Straight vertical divider */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', bgcolor: 'rgba(29,47,65,0.12)', flexShrink: 0, mx: 4, position: 'relative', zIndex: 1 }} />

            {/* Quote */}
            <Box
              component={motion.div}
              variants={ITEM}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}
              style={{ willChange: 'transform' }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: { xs: '5rem', md: '6.5rem' },
                    fontWeight: 900,
                    color: '#1D2F41',
                    lineHeight: 0.8,
                    mb: 2,
                    letterSpacing: '-0.05em',
                  }}
                >
                  &#8220;
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Calibri", sans-serif',
                    fontSize: { xs: '1.15rem', md: '1.35rem' },
                    fontWeight: 300,
                    lineHeight: 1.65,
                    color: '#1D2F41',
                  }}
                >
                  {t('testimonials.quote')}
                </Typography>
              </Box>
              <Box sx={{ mt: 4 }}>
                <Box component="img" src={celsionLogo} alt="Celsion Bank AG" loading="lazy" sx={{ height: '28px', objectFit: 'contain', filter: 'grayscale(100%)', opacity: 0.7 }} />
              </Box>
            </Box>

          </Box>
        </motion.div>

      </Box>
    </Box>
  );
};

export default Testimonials;
